import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'
import type {
  AlarmRecipient,
  AlarmRecipientTestResult,
  AlarmRecipientWrite,
} from '../queries/alarmRecipients.ts'

/**
 * 알림 수신자 쓰기 — `IFC-RO-104`~`107`(v0.9.4 · `ctcells-core/app/views/alarm_recipients.py` · core@d50b8cf4).
 *
 * | 연산 | 경로 | 본문 | 응답 |
 * | --- | --- | --- | --- |
 * | 등록 | `POST /settings/alarm-recipients` | `AlarmRecipientWrite` — 필수 `name` · `kind` · `url` · `min_severity`(`REQUIRED_ON_CREATE`) · 기본 `enabled=true` · `role=all` | 201 `AlarmRecipient` |
 * | 고치기 | `PATCH /settings/alarm-recipients/{id}` | **든 칸만** 바뀐다(병합) · `url` 이 비었거나 없으면 «그대로» · 바뀐 칸이 있으면 `version` +1 | 200 `AlarmRecipient` |
 * | 지우기 | `DELETE /settings/alarm-recipients/{id}` | **본문 없음**(사유 · `command_id` 를 받지 않는다) | 204 |
 * | 시험 발송 | `POST /settings/alarm-recipients/{id}/test` | **본문 없음** · 알람 · 대기 행 · 감사를 안 남긴다 | 200 `AlarmRecipientTestResult` |
 *
 * - 역할: **admin 한정** · 제어락 비대상(`controlToken.ts` 표에 넣지 않는다).
 * - 🔴 422 `REQUEST_VALIDATION_FAIL` 의 `context.reason` 은 **문자열 목록**이다(칸 이름 · 규칙만 · 입력값을 되돌려 싣지 않는다).
 *   `error_codes` 가 카탈로그 밖이면 여기서 거절된다 — 프론트가 카탈로그를 베껴 미리 막지 않는다.
 * - 🔴 **「받은 것 위에 고친 칸만」** — 고치기 본문은 화면이 받은 값과 «달라진 칸만» 싣는다(`alarmRecipientPatch` · `@ctcells/ui`).
 *   서버가 병합이라 다 보내도 지금은 안전하지만, 모르는 칸을 새로 지어 덮는 습관을 들이지 않는다(CLAUDE.md PUT 절).
 * - 🔴 주소(`url`)는 쓰기 전용 — 응답 · 감사 · 로그에 없다. 화면은 칸을 늘 빈 칸으로 연다.
 */

const PATH = '/settings/alarm-recipients'

/** 쓰기 본문에서 멱등키를 뺀 칸 — 훅이 `command_id` 를 짓는다 */
export type AlarmRecipientFields = Omit<AlarmRecipientWrite, 'command_id'>

export const useCreateAlarmRecipient = createCommand<
  { fields: AlarmRecipientFields; commandId?: string },
  AlarmRecipient
>({
  send: ({ fields, commandId }) =>
    orchestrator.request<AlarmRecipient>({
      method: 'POST',
      path: PATH,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      retryOn401: true,
    }),
  invalidates: () => [keys.alarmRecipients.all()],
})

export const useUpdateAlarmRecipient = createCommand<
  { recipientId: string; fields: AlarmRecipientFields; commandId?: string },
  AlarmRecipient
>({
  send: ({ recipientId, fields, commandId }) =>
    orchestrator.request<AlarmRecipient>({
      method: 'PATCH',
      path: `${PATH}/${encodeURIComponent(recipientId)}`,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      retryOn401: true,
    }),
  invalidates: () => [keys.alarmRecipients.all()],
})

export const useDeleteAlarmRecipient = createCommand<{ recipientId: string }, unknown>({
  send: ({ recipientId }) =>
    orchestrator.request<unknown>({
      method: 'DELETE',
      path: `${PATH}/${encodeURIComponent(recipientId)}`,
      retryOn401: true,
    }),
  // 알람의 발송 요약(`Alarm.dispatch`)이 그 이름을 «지운 수신자»로 바꿔 읽는다 — 목록만 다시 받으면 된다
  invalidates: () => [keys.alarmRecipients.all()],
})

/** 시험 발송 — 상태를 바꾸지 않아 무효화할 것이 없다 */
export const useTestAlarmRecipient = createCommand<
  { recipientId: string },
  AlarmRecipientTestResult
>({
  send: ({ recipientId }) =>
    orchestrator.request<AlarmRecipientTestResult>({
      method: 'POST',
      path: `${PATH}/${encodeURIComponent(recipientId)}/test`,
      retryOn401: true,
    }),
  invalidates: () => [],
})
